import { useMemo, useState } from 'react';
import {
  approximateIn,
  decompose,
  isExactlyRepresentable,
  QuantityError,
  suggestExactAlternatives,
  toBaseQuantity,
  type UnitDefinition,
} from '@wms/shared';
import { useTranslation } from 'react-i18next';
import type { ProductUnitItem } from './types';

/** ProductUnitItem（API DTO，factor 为十进制字符串）→ packages/shared 的 UnitDefinition（factor 为 bigint） */
export function toUnitDefinitions(units: readonly ProductUnitItem[]): UnitDefinition[] {
  return units
    .filter((u) => !u.archivedAt)
    .map((u) => ({
      id: u.id,
      code: u.uomCode,
      factorToBase: BigInt(u.factorToBase),
      isBase: u.isBase,
    }));
}

/**
 * 数量显示（架构书 §5 QuantityDisplay）：主显示精确分解（`10 箱 + 8 支`），
 * 次要行灰色小字约值（`≈ 10.33 箱`，带"≈"且永不参与计算，D-05）。
 */
export function QuantityDisplay({
  baseQty,
  units,
}: {
  readonly baseQty: bigint;
  readonly units: readonly UnitDefinition[];
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const base = units.find((u) => u.isBase);
  if (!base) return null;

  const decomposition = decompose(baseQty, units);
  const exact = decomposition.parts
    .map((p) =>
      t('quantity.baseHint', { value: p.count.toString(), unit: p.unit.code }).replace(/^=\s*/, ''),
    )
    .join(t('quantity.exactSeparator'));

  const largest = units.reduce((a, b) => (b.factorToBase > a.factorToBase ? b : a), base);
  const approx = largest.id !== base.id ? approximateIn(baseQty, largest) : null;

  return (
    <div>
      <div>{decomposition.negative ? `-${exact}` : exact}</div>
      {approx !== null && (
        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
          {t('quantity.approximately', { value: approx, unit: largest.code })}
        </div>
      )}
    </div>
  );
}

/**
 * 换算预览（M4 退出标准："1 托 = 40 箱 = 960 支"能在 UI 正确双向换算）。
 * 纯前端演示组件：输入任一配置单位的数量，实时算出基本单位数量与精确分解，
 * 不发起任何写请求——真正的库存写入换算发生在 M5 的入库/出库流程里，
 * 但换算数学本身（packages/shared/quantity.ts）在 M4 这里就已经是同一份实现。
 */
export function ConversionPreview({
  units,
}: {
  readonly units: readonly UnitDefinition[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const base = units.find((u) => u.isBase);
  const [unitId, setUnitId] = useState(base?.id ?? '');
  const [input, setInput] = useState('1');

  const selected = units.find((u) => u.id === unitId) ?? base;

  const result = useMemo(() => {
    if (!selected) return null;
    try {
      const baseQty = toBaseQuantity(input, selected.factorToBase);
      return {
        baseQty,
        error: null as string | null,
        params: undefined as Record<string, string> | undefined,
      };
    } catch (err) {
      if (err instanceof QuantityError) {
        if (err.code === 'QUANTITY.NOT_DIVISIBLE' && base) {
          const alt = suggestExactAlternatives(input, selected.factorToBase);
          return {
            baseQty: null,
            error: err.code,
            params: {
              input,
              unit: selected.code,
              inexact: alt.inexactBase,
              baseUnit: base.code,
              lower: alt.lowerBase.toString(),
              upper: alt.upperBase.toString(),
            },
          };
        }
        return { baseQty: null, error: err.code, params: undefined };
      }
      throw err;
    }
  }, [input, selected, base]);

  const exactOk = selected ? isExactlyRepresentable(input, selected.factorToBase) : true;

  if (!base) return <p className="field-hint">{t('units.noBaseUnit')}</p>;

  return (
    <div className="section">
      <h3>{t('units.conversionPreviewTitle')}</h3>
      <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-start' }}>
        <label className="field" style={{ maxWidth: '140px' }}>
          <span>{t('units.quantity')}</span>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            inputMode="decimal"
            aria-invalid={!exactOk}
          />
        </label>
        <label className="field" style={{ maxWidth: '200px' }}>
          <span>{t('units.unit')}</span>
          <select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
              </option>
            ))}
          </select>
        </label>
      </div>

      {result?.error && (
        <p className="field-error">
          {t(`errors:${result.error}`, { defaultValue: result.error, ...result.params })}
        </p>
      )}
      {result?.baseQty !== null && result?.baseQty !== undefined && (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <QuantityDisplay baseQty={result.baseQty} units={units} />
          <p className="field-hint">
            {t('units.baseQuantityIs', { value: result.baseQty.toString(), unit: base.code })}
          </p>
        </div>
      )}
    </div>
  );
}
